/**
 * PATH: packages/ui/src/components/settings/RoleManagementTab.tsx
 * PURPOSE: Role-Based Access Control management tab for Settings Modal
 *
 * WHY: Org admins need to manage user roles and permissions without leaving
 *      the universal settings modal. This integrates with the RBAC backend.
 *
 * FLOW:
 *   ┌─────────────────┐    ┌──────────────────┐    ┌─────────────────┐
 *   │ Load Members    │───▶│ Select Role      │───▶│ Save Assignment │
 *   │ & Roles         │    │ for User         │    │                 │
 *   └─────────────────┘    └──────────────────┘    └─────────────────┘
 *
 * DEPENDENCIES:
 *   - SettingsApiClient: API contract for persistence
 *   - RBAC API: /api/rbac/org/{id}/members/, /api/rbac/org/{id}/roles/
 *   - ./role-management-tab/*: extracted sub-components and types
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ShieldAlert,
  RefreshCw,
  UserCog,
  CheckCircle2,
  X as XIcon,
  Info,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { isUserAdmin } from './utils'
import type { MemberRole, SystemRole, RolesState, RoleManagementTabProps } from './role-management-tab/role-management-types'
import { MembersTable } from './role-management-tab/MembersTable'

// Re-export for consumers that import from this file
export type { RoleManagementTabProps } from './role-management-tab/role-management-types'

// =============================================================================
// COMPONENT
// =============================================================================

export function RoleManagementTab({
  apiClient,
  organisationId,
  user,
  className,
}: RoleManagementTabProps) {
  const [members, setMembers] = useState<MemberRole[]>([])
  const [roles, setRoles] = useState<RolesState>({ system_roles: [], custom_roles: [] })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savingUserId, setSavingUserId] = useState<number | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [showPermissionsHelp, setShowPermissionsHelp] = useState(false)

  const canManage = isUserAdmin(user)

  // =========================================================================
  // DATA FETCHING
  // =========================================================================

  const fetchData = useCallback(async () => {
    if (!apiClient || !organisationId) return

    setIsLoading(true)
    setError(null)

    try {
      // Strategy: Try RBAC endpoints first, fall back to legacy /permissions/ if RBAC
      // is not seeded (empty member list or 403). This handles orgs that haven't
      // migrated to the new RBAC system yet.

      let membersData: MemberRole[] = []
      let systemRoles: SystemRole[] = []
      let customRoles: SystemRole[] = []

      // Helper: apiClient may be a raw Axios instance (returns { data, status, ... })
      // or a pre-unwrapped client (returns data directly). Handle both.
      const unwrap = (res: any) => res?.data !== undefined && res?.status !== undefined && res?.headers !== undefined ? res.data : res

      // --- Fetch assignable roles (hierarchy-filtered by backend) ---
      try {
        const assignableRes = unwrap(await apiClient.get(`/organisations/${organisationId}/assignable-roles/`))
        const assignableRoles = Array.isArray(assignableRes?.roles) ? assignableRes.roles : (Array.isArray(assignableRes) ? assignableRes : [])
        systemRoles = assignableRoles.map((r: any) => ({
          code: r.code, name: r.name, description: r.description || '',
          hierarchy_level: r.hierarchy_level, type: 'system' as const,
          permissions: r.permissions || r.default_permissions || {},
        }))
      } catch {
        try {
          const rolesRes = unwrap(await apiClient.get(`/rbac/org/${organisationId}/roles/`))
          systemRoles = Array.isArray(rolesRes?.system_roles) ? rolesRes.system_roles : []
          customRoles = Array.isArray(rolesRes?.custom_roles) ? rolesRes.custom_roles : []
        } catch {
          // Roles not available
        }
      }

      // --- Fetch members (RBAC + legacy merged) ---
      // Always fetch both endpoints and merge: RBAC has proper role data,
      // legacy has the complete member list. Members only in the legacy list
      // are shown with "Unassigned" so admins can assign them a role.
      let rbacMembers: MemberRole[] = []
      let legacyMembers: any[] = []

      try {
        const membersRes = unwrap(await apiClient.get(`/rbac/org/${organisationId}/members/`))
        const rawMembers = membersRes?.members ?? (Array.isArray(membersRes) ? membersRes : [])
        rbacMembers = Array.isArray(rawMembers) ? rawMembers : []
      } catch {
        // RBAC members endpoint failed (403 or not seeded)
      }

      try {
        const legacyRes = unwrap(await apiClient.get(`/permissions/members/?tenant=${organisationId}`))
        const rawLegacy = legacyRes?.members ?? (Array.isArray(legacyRes) ? legacyRes : [])
        legacyMembers = Array.isArray(rawLegacy) ? rawLegacy : []
      } catch (legacyErr) {
        if (import.meta.env.DEV) console.error('[RoleManagement] Legacy members fetch failed:', legacyErr)
      }

      // Build a set of user IDs already present in RBAC data
      const rbacUserIds = new Set(rbacMembers.map(m => m.user_id))

      // Transform legacy members not already in RBAC and merge
      const legacyOnly = legacyMembers
        .filter((m: any) => !rbacUserIds.has(m.user_id))
        .map((m: any) => ({
          user_id: m.user_id,
          email: m.email,
          first_name: m.first_name ?? m.username ?? '',
          last_name: m.last_name ?? '',
          role: m.role && typeof m.role === 'object'
            ? m.role
            : {
                code: m.role ?? 'external_guest',
                name: m.role_display ?? m.role ?? 'External / Guest',
                type: 'system' as const,
              },
          assigned_at: m.joined_at ?? '',
          assigned_by: null,
        }))

      membersData = [...rbacMembers, ...legacyOnly]

      setMembers(membersData)
      setRoles({ system_roles: systemRoles, custom_roles: customRoles })

      // Only show error if BOTH sources returned nothing
      if (membersData.length === 0) {
        setError('No team members found. RBAC roles may need to be assigned to organisation members.')
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[RoleManagement] Failed to fetch:', err)
      setError('Unable to load team members and roles')
    } finally {
      setIsLoading(false)
    }
  }, [apiClient, organisationId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // =========================================================================
  // ROLE ASSIGNMENT
  // =========================================================================

  const handleRoleChange = useCallback(async (userId: number, roleCode: string) => {
    if (!apiClient || !organisationId || !canManage) return

    setSavingUserId(userId)
    setError(null)
    setSuccessMsg(null)

    try {
      // RBAC MR 9 — single canonical write path.
      //
      // The pre-MR-9 implementation tried two endpoints in turn:
      //   1. PUT /api/rbac/org/<org>/members/<user>/role/  (canonical)
      //   2. PUT /api/permissions/users/<user>/role/?tenant=<org>
      //
      // Backend MR 7 (qb!3123) deprecated #2 and made it return
      // HTTP 410 Gone. The fallback therefore could never succeed —
      // it just produced a worse error after the canonical PUT had
      // already failed for the same underlying reason (membership
      // gate, RBAC seeding, etc). Dropping it gives the admin a
      // single, accurate error toast instead of two confusing ones.
      //
      // Backend also bidirectionally syncs UserRole <-> Member.role
      // via signals (see authentication/rbac_signals.py) so a single
      // PUT here is enough — both the rich RBAC catalog and the
      // coarse 4-tier OrganisationMember.role land in lockstep.
      await apiClient.put(`/rbac/org/${organisationId}/members/${userId}/role/`, {
        role_code: roleCode,
      })

      const roleName = [...roles.system_roles, ...roles.custom_roles].find(r => r.code === roleCode)?.name || roleCode
      const memberName = members.find(m => m.user_id === userId)?.email || 'User'

      // Update local state
      setMembers(prev => prev.map(m =>
        m.user_id === userId
          ? {
              ...m,
              role: {
                code: roleCode,
                name: roleName,
                type: roles.system_roles.some(r => r.code === roleCode) ? 'system' : 'custom',
              }
            }
          : m
      ))
      setSuccessMsg(`${memberName} is now "${roleName}"`)
      setTimeout(() => setSuccessMsg(null), 4000)
    } catch (err) {
      if (import.meta.env.DEV) console.error('[RoleManagement] Failed to assign role:', err)
      setError('Failed to update role')
    } finally {
      setSavingUserId(null)
    }
  }, [apiClient, organisationId, canManage, roles])

  // =========================================================================
  // RENDER
  // =========================================================================

  if (!organisationId || !apiClient) {
    return (
      <div className={cn('max-w-3xl space-y-6', className)}>
        <div className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30">
          <ShieldAlert className="w-5 h-5 text-slate-400 mt-0.5" />
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-200">Role management unavailable</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Connect an organisation to manage user roles.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const allRoles = [...roles.system_roles, ...roles.custom_roles]

  // RBAC V2 MR 19 FE (2026-05-29) — derive the current user's hierarchy
  // level so MembersTable can do a real privilege comparison instead of
  // the pre-MR-19 "is the code in the assignable allowlist?" heuristic
  // (which false-locked rows for any legacy/transitional code that wasn't
  // in the 7 V2 canonical set — e.g. site_worker for Luc on Bravada).
  // Match the current user by email against the loaded member list; look
  // up their role code in `allRoles` for the hierarchy_level. Either side
  // can be absent (current user might be a Global Admin not in this org's
  // member list, or on a legacy code) — `undefined` flows through to a
  // permissive lock in MembersTable.
  const currentUserMember = members.find(m => m.email === user?.email)
  const currentUserRoleCode = currentUserMember?.role?.code
  const currentUserHierarchy = allRoles.find(r => r.code === currentUserRoleCode)?.hierarchy_level

  return (
    <div className={cn('max-w-3xl space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <UserCog className="w-5 h-5 text-teal-600" />
            Role Management
            <button
              onClick={() => setShowPermissionsHelp(!showPermissionsHelp)}
              className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="View role permissions"
            >
              <Info className={cn('w-4 h-4', showPermissionsHelp ? 'text-teal-600' : 'text-slate-400')} />
            </button>
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Assign roles to control what each team member can access
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={isLoading}
          className="gap-2"
        >
          <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {showPermissionsHelp && (
        <div className="p-4 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10 animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Role Permissions Overview</p>
            <button onClick={() => setShowPermissionsHelp(false)} className="p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5">
              <XIcon className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>
          <div className="space-y-3 text-sm text-slate-500 dark:text-slate-400">
            <div>
              <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Leadership</p>
              <ul className="space-y-0.5 ml-3">
                <li>&bull; <strong>Organisation Owner</strong> &ndash; Full control: all hubs, billing, settings, org management</li>
                <li>&bull; <strong>Director / General Manager</strong> &ndash; All hubs including CFO, billing view, full HR</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Administration</p>
              <ul className="space-y-0.5 ml-3">
                <li>&bull; <strong>Org Administrator</strong> &ndash; All operational hubs, user/settings management. No CFO or billing</li>
                <li>&bull; <strong>Operations Manager</strong> &ndash; Jobs, QA, Workforce, Inventory, Assets. CFO read-only for budget visibility</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Department Heads</p>
              <ul className="space-y-0.5 ml-3">
                <li>&bull; <strong>Finance Manager / CFO</strong> &ndash; Full CFO &amp; Billing access. View-only on Jobs, CRM</li>
                <li>&bull; <strong>Project Manager</strong> &ndash; Jobs, QA, CRM, Quotes, Workforce (team). No CFO or billing</li>
                <li>&bull; <strong>Commercial Manager</strong> &ndash; CRM, Quotes, CFO, Billing. Contract-focused</li>
                <li>&bull; <strong>HR Manager</strong> &ndash; Workforce, HR, Teams management. No financial access</li>
                <li>&bull; <strong>Construction Manager</strong> &ndash; Jobs, QA, Workforce, Assets. No financial access</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Specialists</p>
              <ul className="space-y-0.5 ml-3">
                <li>&bull; <strong>Estimator / QS</strong> &ndash; CRM and Quote Builder admin. No CFO or billing</li>
                <li>&bull; <strong>QA Manager</strong> &ndash; QA admin, document management. No financial access</li>
                <li>&bull; <strong>Safety Officer</strong> &ndash; QA write, HR read. Safety-focused</li>
                <li>&bull; <strong>Scheduler</strong> &ndash; Jobs, Workforce write, schedule-focused</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Office &amp; Support</p>
              <ul className="space-y-0.5 ml-3">
                <li>&bull; <strong>Accounts Officer</strong> &ndash; CFO write, Billing write. Finance support role</li>
                <li>&bull; <strong>HR Officer</strong> &ndash; Workforce, HR, Teams. No financial access</li>
                <li>&bull; <strong>Admin Assistant</strong> &ndash; CRM, Email, QDrive. General office support</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Field &amp; External</p>
              <ul className="space-y-0.5 ml-3">
                <li>&bull; <strong>Site Supervisor</strong> &ndash; Team-level Jobs, QA, Workforce access</li>
                <li>&bull; <strong>Tradesperson / Worker</strong> &ndash; QA entries, own timesheets, team files</li>
                <li>&bull; <strong>Subcontractor</strong> &ndash; Own assigned projects only</li>
                <li>&bull; <strong>Client / Guest</strong> &ndash; View-only on assigned projects. No internal data</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 italic">
              You can only assign roles at or below your own level. Only Global Admins can assign Owner-level roles.
            </p>
          </div>
        </div>
      )}

      {/* Success toast */}
      {successMsg && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-sm font-medium">
          <CheckCircle2 className="w-4 h-4" />
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-auto p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5">
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
          <ShieldAlert className="w-5 h-5 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <MembersTable
        members={members}
        roles={allRoles}
        canManage={canManage}
        savingUserId={savingUserId}
        user={user}
        isLoading={isLoading}
        onRoleChange={handleRoleChange}
        currentUserHierarchy={currentUserHierarchy}
      />
    </div>
  )
}

export default RoleManagementTab
